import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { ActiveUser } from '../models/user-role.model';

import { appEnvironment } from '../app-environment';

interface BackendUserResponse {
  id?: number;
  username?: string;
  email?: string;
  role?: string;
}

interface BackendAuthResponse {
  message?: string;
  user?: BackendUserResponse;
  id?: number;
  username?: string;
  email?: string;
  role?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storageKey = 'daniiplace.activeUser';
  private readonly apiUrl = `${appEnvironment.apiBaseUrl}/auth`;

  private readonly guest: ActiveUser = { username: 'Gast', role: 'guest' };
  private readonly activeUserSignal = signal<ActiveUser>(this.loadUserFromStorage());

  readonly activeUser = this.activeUserSignal.asReadonly();
  readonly isLoggedIn = computed(() => this.activeUser().role !== 'guest');

  login(usernameOrEmail: string, password: string): Observable<ActiveUser> {
    return this.http.post<BackendAuthResponse>(`${this.apiUrl}/login`, {
      usernameOrEmail,
      password
    }).pipe(
      map(response => this.mapBackendUser(response)),
      tap(user => this.setActiveUser(user))
    );
  }

  logout(): void {
    localStorage.removeItem(this.storageKey);
    this.activeUserSignal.set(this.guest);
  }

  canUseCreatorSpace(): boolean {
    return this.isCreatorSpaceRole(this.activeUser().role);
  }

  isAdmin(): boolean {
    return this.normalizeRole(this.activeUser().role) === 'admin';
  }

  private setActiveUser(user: ActiveUser): void {
    localStorage.setItem(this.storageKey, JSON.stringify(user));
    this.activeUserSignal.set(user);
  }

  private loadUserFromStorage(): ActiveUser {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return this.guest;
    }

    try {
      const parsed = JSON.parse(raw) as ActiveUser;
      if (!parsed.username || !parsed.role) {
        return this.guest;
      }

      return parsed;
    } catch {
      return this.guest;
    }
  }

  private mapBackendUser(response: BackendAuthResponse): ActiveUser {
    const user = response.user ?? response;

    return {
      id: user.id,
      username: user.username ?? 'Unbekannt',
      email: user.email,
      role: user.role ?? 'user'
    };
  }

  private isCreatorSpaceRole(role: string): boolean {
    return ['admin', 'creator', 'contentcreator', 'moderator'].includes(this.normalizeRole(role));
  }

  private normalizeRole(role: string): string {
    return role
      .trim()
      .toLowerCase()
      .replace(/[-_\s]/g, '');
  }
}
